<template>
  <el-drawer v-model="visible" title="站内信" :size="480" direction="rtl" @close="handleClose">
    <template #header>
      <div class="drawer-header">
        <div class="header-top">
          <span class="drawer-title">站内信</span>
        </div>
        <div class="header-filters">
          <div class="filter-group">
            <el-button-group>
              <el-button :type="currentTab === 'all' ? 'primary' : ''" size="small" @click="switchTab('all')">全部</el-button>
              <el-button :type="currentTab === 'unread' ? 'primary' : ''" size="small" @click="switchTab('unread')">未读</el-button>
              <el-button :type="currentTab === 'starred' ? 'primary' : ''" size="small" @click="switchTab('starred')">星标</el-button>
            </el-button-group>
            <el-select v-model="currentMsgType" size="small" class="filter-select" placeholder="消息类型" clearable>
              <el-option label="全部" :value="-1" />
              <el-option label="通知" :value="0" />
              <el-option label="公告" :value="1" />
              <el-option label="私信" :value="2" />
              <el-option label="系统消息" :value="3" />
              <el-option label="待办事项" :value="4" />
            </el-select>
            <el-select v-model="currentExpireStatus" size="small" class="filter-select" placeholder="过期状态" clearable>
              <el-option label="全部" :value="-1" />
              <el-option label="未过期" :value="0" />
              <el-option label="已过期" :value="1" />
            </el-select>
            <el-select v-model="currentPriority" size="small" class="filter-select" placeholder="优先级" clearable>
              <el-option label="全部" :value="-1" />
              <el-option label="普通" :value="0" />
              <el-option label="重要" :value="1" />
              <el-option label="紧急" :value="2" />
            </el-select>
          </div>
          <div class="filter-actions">
            <el-link v-if="hasUnreadMessages" type="primary" :underline="false" @click="handleMarkAllAsRead">全部已读</el-link>
          </div>
        </div>
      </div>
    </template>

    <div class="inbox-container" v-loading="loading">
      <div ref="scrollContainer" class="message-list" @scroll="handleScroll">
        <div v-for="message in messages" :key="message.id" class="message-item" :class="{ unread: message.status === 0, starred: message.isStarred }" @click="handleMessageClick(message)">
          <div class="message-header">
            <div class="message-title-row">
              <el-icon v-if="message.isStarred" class="star-icon" color="#f56c6c">
                <StarFilled />
              </el-icon>
              <span class="message-title" :class="{ 'unread-title': message.status === 0 }">
                {{ message.title || '无标题' }}
              </span>
              <el-badge v-if="message.status === 0" :value="''" class="unread-badge" is-dot />
            </div>
            <div class="message-actions">
              <el-dropdown trigger="click" @command="handleAction">
                <el-icon class="action-icon" @click.stop><MoreFilled /></el-icon>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item v-if="message.status === 0" :command="{ action: 'read', id: message.id }">标记已读</el-dropdown-item>
                    <el-dropdown-item :command="{ action: 'star', id: message.id, isStarred: !message.isStarred }">
                      {{ message.isStarred ? '取消星标' : '添加星标' }}
                    </el-dropdown-item>
                    <el-dropdown-item :command="{ action: 'delete', id: message.id }" divided>删除</el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>

          <div class="message-time">{{ formatTime(message.sendTime) }}</div>
        </div>

        <div v-if="messages.length === 0 && !loading" class="empty-state">
          <el-empty description="暂无消息" />
        </div>

        <div v-if="loading && messages.length > 0" class="loading-more">
          <el-icon class="is-loading"><Loading /></el-icon>
          <span>加载中...</span>
        </div>

        <div v-if="!hasMore && messages.length > 0" class="no-more">没有更多了</div>
      </div>
    </div>

    <el-drawer v-model="showDetail" title="消息详情" :size="600" direction="rtl" :append-to-body="true">
      <div v-if="currentMessageDetail" class="message-detail">
        <div class="detail-header">
          <h3 class="detail-title">{{ currentMessageDetail.title }}</h3>
          <div class="detail-meta">
            <span class="detail-time">发送时间：{{ formatTime(currentMessageDetail.sendTime) }}</span>
            <span v-if="currentMessageDetail.readTime" class="detail-time"> 阅读时间：{{ formatTime(currentMessageDetail.readTime) }} </span>
          </div>
        </div>

        <div class="message-content-wrapper">
          <div class="message-content" v-html="getMessageHtml(currentMessageDetail.content, currentMessageDetail.contentFormat ?? 1)" @click="handleContentClick"></div>
        </div>

        <div v-if="currentMessageDetail.attachments" class="detail-attachments">
          <div class="attachments-title">附件：</div>
          <div class="attachments-list">
            {{ currentMessageDetail.attachments }}
          </div>
        </div>

        <div class="detail-actions">
          <el-button v-if="currentMessageDetail.status === 0" type="primary" @click="handleMarkAsRead(currentMessageDetail.id)">标记已读</el-button>
          <el-button :type="currentMessageDetail.isStarred ? 'warning' : ''" @click="handleToggleStarred(currentMessageDetail.id, !currentMessageDetail.isStarred)">
            <el-icon><Star /></el-icon>
            {{ currentMessageDetail.isStarred ? '取消星标' : '添加星标' }}
          </el-button>
          <el-button type="danger" @click="handleDelete(currentMessageDetail.id)">删除</el-button>
        </div>
      </div>
    </el-drawer>
  </el-drawer>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useInboxStore } from '@/store/inbox'
import { newCall, newRequest } from '@/util/http'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ContentParser } from '@/util/contentParser'
import { useRouter } from 'vue-router'

const INBOX_LINK_TYPE_TO_PATH = {
  HAZARD: '/core/hazard/look',
  CHECK_REGULAR: '/core/check/checkTask/execute',
  CHECK_RISK: '/core/check/riskTask/execute',
  RISK_EVAL: '/core/risk/risk/estimate',
  RISK_EVAL_REVIEW: '/core/risk/risk/look',
  TRAIN_REGULAR: '/trainExam/onlineTrain/regular/look',
  TRAIN_RISK: '/trainExam/onlineTrain/risk/look',
  TRAIN_OFFLINE: '/trainExam/offlineTrain/train/look',
  EXAM: '/trainExam/exam/exam/detail',
  TICKET_REGULAR: '/ticket/job/regular/look',
  TICKET_SPECIAL: '/ticket/job/special/look',
  TICKET_OTHER: '/ticket/regular/look',
  VIOLATION: '/behavior/violation/look',
  VIOLATION_TRAIN: '/behavior/violationScore/train',
  SCORE_GOODS_REDEEM: '/score/goods/redeemDetail',
  SPECIAL_EQUIP: '/specialEquip/equipment/look',
  SPECIAL_EQUIP_ACCESSORY: '/specialEquip/accessory/look',
  SAFETY_VALVE: '/specialEquip_0001/valve/look',
  SPECIAL_EQUIP_0001: '/specialEquip_0001/equipment/look',
  SPECIAL_EQUIP_0001_PRESSURE: '/specialEquip_0001/pressure/look',
  SPECIAL_EQUIP_0001_BURST: '/specialEquip_0001/burst/look',
  SPECIAL_EQUIP_0001_SCHEDULE: '/specialEquip_0001/schedule',
  SPECIAL_EQUIP_0001_SCHEDULE_REPORT: '/specialEquip_0001/schedule/report'
}

const router = useRouter()

const inboxStore = useInboxStore()

const visible = defineModel({ default: false })

const messages = ref([])
const loading = ref(false)
const hasMore = ref(true)
const currentPage = ref(0)
const listFilters = ref({ status: null, msgType: null, priority: null, expireStatus: null })

const currentTab = ref('unread')
const currentMsgType = ref(null)
const currentExpireStatus = ref(null)
const currentPriority = ref(null)
const currentMessageDetail = ref(null)
const showDetail = ref(false)

const hasUnreadMessages = computed(() => messages.value.some((message) => message.status === 0))

watch(visible, (newVal) => {
  if (newVal) {
    loadMessages()
  } else {
    resetList()
    showDetail.value = false
    currentMessageDetail.value = null
  }
})

watch(currentTab, () => {
  loadMessages()
})

watch([currentMsgType, currentExpireStatus, currentPriority], () => {
  if (currentTab.value !== 'starred') {
    loadMessages()
  }
})

function switchTab(tab) {
  currentTab.value = tab
}

function resetList() {
  messages.value = []
  currentPage.value = 0
  hasMore.value = true
  listFilters.value = { status: null, msgType: null, priority: null, expireStatus: null }
}

function loadMessages() {
  resetList()
  if (currentTab.value === 'starred') {
    loadStarredMessages()
  } else {
    const status = currentTab.value === 'unread' ? 0 : null
    const msgType = currentMsgType.value === -1 ? null : currentMsgType.value
    const priority = currentPriority.value === -1 ? null : currentPriority.value
    const expireStatus = currentExpireStatus.value === -1 ? null : currentExpireStatus.value
    fetchMessages(status, msgType, priority, expireStatus, 0, 20)
  }
}

async function fetchMessages(status = null, msgType = null, priority = null, expireStatus = null, page = 0, size = 20) {
  if (loading.value) return

  loading.value = true
  listFilters.value = { status, msgType, priority, expireStatus }

  const query = { page, size }
  if (status !== null) query.status = status
  if (msgType !== null && msgType !== -1) query.msgType = msgType
  if (priority !== null && priority !== -1) query.priority = priority
  if (expireStatus !== null && expireStatus !== -1) query.expireStatus = expireStatus

  const { code, data, message } = await newCall({ url: 'inbox/messages', query })
  if (code === 0) {
    if (page === 0) {
      messages.value = data.content || []
    } else {
      messages.value = [...messages.value, ...(data.content || [])]
    }
    currentPage.value = data.currentPage || 0
    hasMore.value = currentPage.value < (data.totalPages || 0) - 1
  } else {
    ElMessage.error(message)
  }
  loading.value = false
}

async function loadStarredMessages() {
  loading.value = true

  const { code, data, message } = await newCall({ url: 'inbox/starred' })
  if (code === 0) {
    messages.value = data || []
    hasMore.value = false
  } else {
    ElMessage.error(message)
  }

  loading.value = false
}

async function markAsRead(id, onSuccess) {
  const { code, message } = await newCall({ url: 'inbox/message/read', method: 'POST', query: { id } })
  if (code === 0) {
    const message = messages.value.find((m) => m.id === id)
    if (message) {
      const wasUnread = message.status === 0
      message.status = 1
      if (wasUnread) inboxStore.adjustUnreadCount(-1)
    }
    onSuccess?.()
  } else {
    ElMessage.error(message)
  }
}

async function toggleStarred(id, isStarred, onSuccess) {
  const { code, message } = await newCall({ url: 'inbox/message/star', method: 'POST', query: { id, isStarred } })
  if (code === 0) {
    const message = messages.value.find((m) => m.id === id)
    if (message) message.isStarred = isStarred
    onSuccess?.()
  } else {
    ElMessage.error(message)
  }
}

function handleScroll(event) {
  if (currentTab.value === 'starred') return

  const container = event.target
  const scrollTop = container.scrollTop
  const scrollHeight = container.scrollHeight
  const clientHeight = container.clientHeight

  if (scrollHeight - scrollTop - clientHeight < 100 && hasMore.value && !loading.value) {
    if (!hasMore.value || loading.value) return
    const { status, msgType, priority, expireStatus } = listFilters.value
    fetchMessages(status, msgType, priority, expireStatus, currentPage.value + 1, 20)
  }
}

function handleMessageClick({ id, status }) {
  if (status === 0) {
    markAsRead(id, () => fetchMessageDetail(id))
  } else {
    fetchMessageDetail(id)
  }
}

function fetchMessageDetail(id) {
  newRequest({
    url: `inbox/message/${id}`,
    loading: true,
    success: (data) => {
      currentMessageDetail.value = data
      showDetail.value = true
      const message = messages.value.find((m) => m.id === id)
      if (message) {
        message.status = data.status
      }
    }
  })
}

function handleAction({ action, id, isStarred }) {
  switch (action) {
    case 'read':
      handleMarkAsRead(id)
      break
    case 'star':
      handleToggleStarred(id, isStarred)
      break
    case 'delete':
      handleDelete(id)
      break
  }
}

function handleMarkAsRead(id) {
  markAsRead(id, () => {
    ElMessage.success('已标记为已读')
    if (currentMessageDetail.value && currentMessageDetail.value.id === id) {
      currentMessageDetail.value.status = 1
    }
  })
}

function handleToggleStarred(id, isStarred) {
  toggleStarred(id, isStarred, () => {
    ElMessage.success(isStarred ? '已添加星标' : '已取消星标')
    if (currentMessageDetail.value && currentMessageDetail.value.id === id) {
      currentMessageDetail.value.isStarred = isStarred
    }
  })
}

function handleDelete(id) {
  ElMessageBox.confirm('确定要删除这条消息吗？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(async () => {
      const { code, message } = await newCall({ url: 'inbox/message/delete', method: 'POST', query: { id } })
      if (code === 0) {
        ElMessage.success('删除成功')
        const deletedMessage = messages.value.find((m) => m.id === id)
        const wasUnread = deletedMessage?.status === 0
        messages.value = messages.value.filter((m) => m.id !== id)
        if (wasUnread) inboxStore.adjustUnreadCount(-1)
        if (currentMessageDetail.value && currentMessageDetail.value.id === id) {
          showDetail.value = false
          currentMessageDetail.value = null
        }
      } else {
        ElMessage.error(message)
      }
    })
    .catch(() => {})
}

async function handleMarkAllAsRead() {
  const { code, data, message } = await newCall({ url: 'inbox/message/markAllAsRead', method: 'POST' })
  if (code === 0) {
    messages.value.forEach((m) => {
      if (m.status === 0) m.status = 1
    })
    inboxStore.updateUnreadCount(0)
    const updatedCount = data.updatedCount || 0
    if (updatedCount === 0) {
      ElMessage.info('没有未读消息')
    } else {
      ElMessage.success(`已标记 ${updatedCount} 条消息为已读`)
    }
  } else {
    ElMessage.error(message)
  }
}

function formatTime(time) {
  if (!time) return ''
  const date = new Date(time.replace(' ', 'T') + '+08:00')
  const now = new Date()
  const diff = now - date
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))

  if (diff < 0) {
    return '刚刚'
  }

  if (days === 0) {
    const hours = Math.floor(diff / (1000 * 60 * 60))
    if (hours === 0) {
      const minutes = Math.floor(diff / (1000 * 60))
      return minutes <= 0 ? '刚刚' : `${minutes}分钟前`
    }
    return `${hours}小时前`
  } else if (days === 1) {
    return '昨天 ' + date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
  } else if (days < 7) {
    return `${days}天前`
  } else {
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
}

function getMessageHtml(content, contentFormat) {
  if (!content) return ''
  return ContentParser.parseToHtml(content, contentFormat ?? ContentParser.ContentFormat.MARKDOWN)
}

function handleContentClick(event) {
  const target = event.target
  if (target.classList.contains('message-link')) {
    event.preventDefault()
    event.stopPropagation()
    ContentParser.handleLinkClick(target, {
      onAction: (action) => {
        handleMessageAction(action)
      },
      onRoute: ({ path, query }) => {
        showDetail.value = false
        currentMessageDetail.value = null
        visible.value = false
        router.push({ path: INBOX_LINK_TYPE_TO_PATH[path], query })
      }
    })
  }
}

function handleMessageAction(action) {
  console.log('消息操作:', action)
  ElMessage.info(`执行操作: ${action}`)
}

function handleClose() {
  visible.value = false
  inboxStore.fetchUnreadCount()
}
</script>

<style lang="scss" scoped>
.drawer-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding-right: 8px;
}

.header-top {
  display: flex;
  align-items: center;
  min-width: 0;
}

.drawer-title {
  font-size: 18px;
  font-weight: bold;
  flex-shrink: 0;
  white-space: nowrap;
}

.header-filters {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.filter-group {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.filter-select {
  width: 130px;
  flex-shrink: 0;
}

.filter-actions {
  display: flex;
  align-items: center;
  margin-top: 8px;
}

.inbox-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.message-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 0;
  box-sizing: border-box;
}

.message-item {
  padding: 12px 16px;
  border-bottom: 1px solid var(--el-border-color-lighter);
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--el-fill-color-light);
  }

  &.unread {
    background-color: var(--el-color-info-light-9);
  }

  &.starred {
    border-left: 3px solid var(--el-color-warning);
  }
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.message-title-row {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 6px;
}

.star-icon {
  flex-shrink: 0;
}

.message-title {
  flex: 1;
  font-size: 14px;
  color: var(--el-text-color-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.unread-title {
    font-weight: 600;
  }
}

.unread-badge {
  flex-shrink: 0;
}

.message-actions {
  flex-shrink: 0;
  margin-left: 10px;
}

.action-icon {
  cursor: pointer;
  color: var(--el-text-color-secondary);
  font-size: 16px;

  &:hover {
    color: var(--el-text-color-primary);
  }
}

.message-time {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  margin-top: 4px;
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
}

.loading-more,
.no-more {
  text-align: center;
  padding: 20px;
  color: var(--el-text-color-secondary);
  font-size: 12px;
}

.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.message-detail {
  padding: 20px;
}

.detail-header {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.detail-title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 12px;
  color: var(--el-text-color-primary);
}

.detail-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-time {
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.detail-content {
  min-height: 200px;
  padding: 16px;
  background-color: var(--el-fill-color-lighter);
  border-radius: 4px;
  margin-bottom: 20px;
  line-height: 1.6;
  color: var(--el-text-color-primary);
  white-space: pre-wrap;
  word-break: break-word;
}

.message-content-wrapper {
  margin-bottom: 20px;
}

.message-content {
  min-height: 100px;
  padding: 16px;
  background-color: var(--el-fill-color-lighter);
  border-radius: 4px;
  line-height: 1.6;
  color: var(--el-text-color-primary);
  word-break: break-word;

  :deep(.message-link) {
    color: var(--el-color-primary);
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: var(--el-color-primary-light-3);
      text-decoration: underline;
    }

    &.message-action {
      color: var(--el-color-success);

      &:hover {
        color: var(--el-color-success-light-3);
      }
    }
  }

  :deep(strong) {
    font-weight: 600;
    color: var(--el-text-color-primary);
  }
}

.detail-attachments {
  margin-bottom: 20px;
}

.attachments-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--el-text-color-primary);
}

.attachments-list {
  font-size: 14px;
  color: var(--el-text-color-regular);
}

.detail-actions {
  display: flex;
  gap: 10px;
  padding-top: 20px;
  border-top: 1px solid var(--el-border-color-lighter);
}
</style>
